import { useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import { usePermissions } from "hooks/usePermissions"
import useActiveGuide from "hooks/useActiveClient"
import { guideBookingStatus } from "store/bookings"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import { MODULES, ACTIONS } from "utils/permissions"
import AdjustBookingPriceModal from "ui/Booking/Details/Body/PaymentSummary/Modals/AdjustBookingPrice"
import CancelBookingModal from "../../../BookingActions/CancelBookingModal"
import DeclineBookingModal from "../../../BookingActions/DeclineBookingModal"
import { deriveCheckInState } from "../../../CheckIn/checkInUtils"
import BookingTag from "../../../BookingTag"
import ExperienceCard from "../ExperienceCard/ExperienceCard"
import ExperienceCardSkeleton from "../ExperienceCard/ExperienceCardSkeleton"
import { useExperienceExtras } from "../../useExperienceExtras"
import styles from "../../index.module.css"

// The Cancel Booking modal's shape: `id` (numeric booking id) loads the refund breakdown, `uuid`
// submits the cancel; the rest is display.
const toCancelBooking = (reservation) => ({
  id: reservation.bookingId,
  uuid: reservation.uuid,
  title: reservation.experience?.title,
  image: reservation.experience?.imageUrl,
  booking_id: reservation.reservationId,
  booked_on: reservation.created,
  client_name: reservation.client?.name,
  client_email: reservation.client?.email
})

// One experience of an order: its accordion with its own price, waivers and answers, and the
// actions that act on this booking alone: Adjust Price and Cancel, or Accept and Decline while it is
// a request (QA Ticketing #55). Payments, Collect Payment and every other action belong to the
// whole reservation. `onChange` reloads it once an
// action went through.
const OrderExperience = ({ entry, onChange }) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState(null)
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const dispatch = useDispatch()
  const { isActive: isGuideActive } = useActiveGuide()
  const { waivers, answers, isLoading: isExtrasLoading, isFailed: isExtrasFailed, reload: reloadExtras } = useExperienceExtras(
    entry.uuid,
    isOpen && Boolean(entry.data)
  )

  // Still loading: the card's skeleton with what the order already says about it (its number, date,
  // start time and status), so the guide can read the order while it loads (QA #58). Failed: that
  // summary with a retry.
  if (!entry.data) {
    const { title, bookingNumber, bookedDate, startTime, status } = entry.booking || {}
    const startsAt = bookedDate && startTime ? dayjs(`${bookedDate} ${startTime}`) : null
    const when = [
      bookedDate ? dayjs(bookedDate).format("MMM D, YYYY") : null,
      startsAt?.isValid() ? startsAt.format("h:mm A") : null
    ].filter(Boolean)
    const statusTag = status ? (
      <span>
        <BookingTag color={status === "canceled" ? "error" : "blue"}>{status.charAt(0).toUpperCase() + status.slice(1)}</BookingTag>
      </span>
    ) : null
    const meta = [bookingNumber, ...when].filter(Boolean).join(" · ")
    if (!entry.error) return <ExperienceCardSkeleton title={title} statusTag={statusTag} meta={meta} />
    return (
      <Flex align="center" justify="space-between" gap={12} className={styles.card}>
        <Flex vertical gap={4}>
          <span className={styles.expTitle}>{title}</span>
          {statusTag}
          <span className={styles.expCode}>{meta}</span>
        </Flex>
        <Flex align="center" gap={8}>
          <span className={styles.metaText}>{t("booking.reservation_detail.experience_load_failed")}</span>
          <Button size="small" shape="round" onClick={() => onChange(entry.uuid)}>
            {t("booking.reservation_detail.retry")}
          </Button>
        </Flex>
      </Flex>
    )
  }

  const reservation = entry.data
  const isRequest = reservation.rawStatus === "requested"
  const isCompleted = reservation.rawStatus === "completed"
  const isCanceled = reservation.rawStatus === "canceled"
  const canEditBookings = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)
  // The same rules as a single reservation's Cancel and Adjust Booking Price, applied to this booking.
  const canCancel = reservation.canEdit && !isRequest && !isCompleted && !isCanceled
  const canAdjustPrice = canEditBookings && !isCanceled && Boolean(reservation.payments)
  const isMarketplaceBooking = reservation.bookingSource === "marketplace" || reservation.bookingSource === "website"

  const closeAndRefresh = () => {
    setOpenModal(null)
    onChange(entry.uuid)
  }

  // The same rules as a single reservation's header Accept/Decline: an expired request or a
  // deactivated guide can't act on it.
  const isRequestActionDisabled = reservation.isExpired || !isGuideActive
  const setRequestStatus = async (status, reason) => {
    if (isSubmittingRequest) return
    setIsSubmittingRequest(true)
    try {
      await dispatch(guideBookingStatus(reservation.uuid, status, reason))
      closeAndRefresh()
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const checkedInCount = reservation.checkedInCount ?? 0
  const totalGuests = reservation.totalGuests ?? 0

  return (
    <>
      <ExperienceCard
        experience={reservation.experience}
        waivers={waivers}
        waiversLoading={isExtrasLoading}
        answers={answers}
        answersLoading={isExtrasLoading}
        addOns={reservation.addOns}
        bookingUuid={reservation.uuid}
        onRefresh={reloadExtras}
        checkInState={reservation.checkInState || deriveCheckInState(checkedInCount, totalGuests, reservation.checkedOutCount ?? 0)}
        checkedInCount={checkedInCount}
        checkedOutCount={reservation.checkedOutCount ?? 0}
        totalGuests={totalGuests}
        // Shown directly on the experience, not behind a menu.
        onAdjustPrice={canAdjustPrice ? () => setOpenModal("adjust_price") : undefined}
        onCancel={canCancel ? () => setOpenModal("cancel") : undefined}
        onAccept={isRequest ? () => setOpenModal("accept") : undefined}
        onDecline={isRequest ? () => setOpenModal("decline") : undefined}
        isRequestActionDisabled={isRequestActionDisabled}
        requestActionsDisabledReason={reservation.isExpired ? t("client.client_booking.trip_time_has_passed") : undefined}
        isStatusShown
        onExpandChange={setIsOpen}
      >
        {isExtrasFailed && (
          <Flex align="center" gap={8} className="mt-s">
            <span className={styles.metaText}>{t("booking.reservation_detail.experience_load_failed")}</span>
            <Button size="small" shape="round" onClick={reloadExtras}>
              {t("booking.reservation_detail.retry")}
            </Button>
          </Flex>
        )}
      </ExperienceCard>

      {openModal === "adjust_price" && (
        <AdjustBookingPriceModal
          onOpen
          onClose={closeAndRefresh}
          bookingUUID={reservation.uuid}
          tripUUID={reservation.tripUUID}
          currentPrice={reservation.payments.total}
          paidAmount={reservation.payments.paidAmount}
          isMarketplaceBooking={isMarketplaceBooking}
          paymentOption={reservation.paymentOption}
        />
      )}
      {openModal === "accept" && (
        <BookingConfirmationModal
          open
          bookingConfirmStatus="accept"
          clientName={reservation.client?.name}
          tripDate={reservation.date}
          handleCancel={() => setOpenModal(null)}
          handleConfirm={() => setRequestStatus("booked")}
          loading={isSubmittingRequest}
        />
      )}
      <DeclineBookingModal
        open={openModal === "decline"}
        onCancel={() => setOpenModal(null)}
        onConfirm={(reason) => setRequestStatus("canceled", reason)}
        clientName={reservation.client?.name}
        tripDate={reservation.date}
        isSubmitting={isSubmittingRequest}
      />
      {openModal === "cancel" && (
        <CancelBookingModal open onCancel={() => setOpenModal(null)} onConfirm={closeAndRefresh} bookings={[toCancelBooking(reservation)]} />
      )}
    </>
  )
}

OrderExperience.propTypes = {
  // useOrderExperiences' entry: the mapped reservation once loaded (`data`), else the order's
  // summary of it (`booking`) while loading or after `error`.
  entry: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    data: PropTypes.object,
    booking: PropTypes.shape({
      title: PropTypes.string,
      bookingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      bookedDate: PropTypes.string,
      startTime: PropTypes.string,
      status: PropTypes.string
    }),
    isLoading: PropTypes.bool,
    error: PropTypes.object
  }).isRequired,
  onChange: PropTypes.func.isRequired
}

export default OrderExperience
